import { useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, FileText, FolderOpen, Users, Tag, Mail, LogOut, ChevronLeft, Calendar, MessageSquare, Settings, Layers, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Toaster } from "@/components/ui/sonner";

const navItems = [
  { label: "Tableau de bord", href: "/admin", icon: LayoutDashboard },
  { label: "Articles", href: "/admin/articles", icon: FileText },
  { label: "Médiathèque", href: "/admin/mediatheque", icon: Play },
  { label: "Calendrier", href: "/admin/calendrier", icon: Calendar },
  { label: "Rubriques", href: "/admin/rubriques", icon: Layers },
  { label: "Catégories", href: "/admin/categories", icon: FolderOpen },
  { label: "Auteurs", href: "/admin/auteurs", icon: Users },
  { label: "Tags", href: "/admin/tags", icon: Tag },
  { label: "Newsletter", href: "/admin/newsletter", icon: Mail },
  { label: "Messages", href: "/admin/messages", icon: MessageSquare },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [user, loading, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center font-body"><p>Chargement...</p></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen flex font-body" data-testid="admin-layout">
      <Toaster position="top-right" />
      <aside className="w-64 bg-[#0f2b4c] flex flex-col shrink-0" data-testid="admin-sidebar">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
            <ChevronLeft className="h-4 w-4" /> Voir le site
          </Link>
          <h2 className="font-heading text-xl mt-4 text-white">OFA<span className="text-sky-400">Lycée</span></h2>
          <p className="text-xs text-white/40">Administration</p>
        </div>
        <Separator className="bg-white/10" />
        <nav className="flex-1 p-3 space-y-0.5">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href || (item.href !== "/admin" && location.pathname.startsWith(item.href));
            return (
              <Link key={item.href} to={item.href}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-sm transition-all ${isActive ? "bg-white/15 text-white" : "text-white/60 hover:text-white hover:bg-white/8"}`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <item.icon className="h-4 w-4" />{item.label}
              </Link>
            );
          })}
        </nav>
        <Separator className="bg-white/10" />
        <div className="p-4">
          <Link to="/admin/profil" className="flex items-center gap-3 mb-3 p-1 -m-1 rounded-sm hover:bg-white/8 transition-colors" data-testid="admin-profile-link">
            <div className="h-8 w-8 rounded-full bg-sky-400/20 flex items-center justify-center text-sky-400 text-sm font-medium">{user.name?.[0]}</div>
            <div><p className="text-sm font-medium truncate text-white">{user.name}</p><p className="text-xs text-white/40">{user.role === "admin" ? "Administrateur" : user.role}</p></div>
          </Link>
          <div className="flex gap-1">
            <Link to="/admin/profil" className="flex-1">
              <Button variant="ghost" size="sm" className="w-full justify-start text-white/50 hover:text-white hover:bg-white/8" data-testid="admin-settings-btn">
                <Settings className="h-4 w-4 mr-2" /> Profil
              </Button>
            </Link>
            <Button variant="ghost" size="sm" className="text-white/50 hover:text-white hover:bg-white/8" onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto bg-slate-50/50"><div className="p-6 md:p-10"><Outlet /></div></main>
    </div>
  );
}
